import type { RepositoryFailure } from "../contracts";

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  status?: unknown;
  details?: unknown;
}

const asErrorLike = (error: unknown): ErrorLike =>
  typeof error === "object" && error !== null ? (error as ErrorLike) : {};

const text = (value: unknown) => (typeof value === "string" ? value : "");
const numericStatus = (value: unknown) =>
  typeof value === "number" ? value : typeof value === "string" ? Number(value) : undefined;

export function translateSupabaseError(
  error: unknown,
  fallbackMessage = "The local persistence operation failed.",
): RepositoryFailure {
  const candidate = asErrorLike(error);
  const code = text(candidate.code);
  const message = text(candidate.message) || fallbackMessage;
  const status = numericStatus(candidate.status);
  const details = text(candidate.details);

  if (/insufficient_assurance|assurance level 2/iu.test(`${message} ${details}`)) {
    return { code: "insufficient_assurance", message, retryable: false, cause: error };
  }

  if (["23505", "23P01", "55000"].includes(code)) {
    return { code: "conflict", message, retryable: false, cause: error };
  }
  if (["PGRST116", "P0002"].includes(code)) {
    return { code: "not_found", message, retryable: false, cause: error };
  }
  if (["42501", "PGRST301"].includes(code) || status === 401 || status === 403) {
    return { code: "permission_denied", message, retryable: false, cause: error };
  }
  if (["23503", "23514", "22P02", "PGRST102"].includes(code) || status === 400 || status === 422) {
    return { code: "validation_failure", message, retryable: false, cause: error };
  }
  if (["40001", "40P01", "57014"].includes(code) || (status !== undefined && status >= 500)) {
    return { code: "persistence_failure", message, retryable: true, cause: error };
  }
  return { code: "persistence_failure", message, retryable: false, cause: error };
}

export const revisionConflict = (message = "The record changed before this update was saved."): RepositoryFailure => ({
  code: "conflict",
  message,
  retryable: false,
});

export const missingRecord = (message = "The requested record was not found."): RepositoryFailure => ({
  code: "not_found",
  message,
  retryable: false,
});
